import React, { useState } from 'react'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import useConfirmationAlert from '../../../../hooks/componentHooks/useConfirmationAlert'
import { gradientColors, textColors } from '../../../Common/Color/Color'
import { fontSizes } from '../../../Common/Typography/TypographyStyles'
import TaskForm from '../TaskForm/'
import { Task } from '../../../../redux/task/types'

import { useSelector } from 'react-redux'
import { RootState } from '../../../../redux/store'

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface TaskUpdateModalProps {
  taskDetails?: Task
}

const TaskUpdateModal: React.FC<TaskUpdateModalProps> = ({ taskDetails }) => {
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))
  const [open, setOpen] = useState<boolean>(false)
  const [formHasChanges, setFormHasChanges] = useState<boolean>(false)

  const handleOpenModal = () => {
    setOpen(true)
  }

  const handleFormChanges = React.useCallback((hasChanges: boolean) => {
    setFormHasChanges(hasChanges)
  }, [])

  const { ConfirmationAlert, openConfirmationAlert } = useConfirmationAlert()

  const handleCloseModal = () => {
    if (formHasChanges) {
      openConfirmationAlert({
        title: 'Discard changes',
        text: 'Are you sure you want to discard your changes?',
        onConfirm: () => {
          setOpen(false)
        },
      })
    } else {
      setOpen(false)
    }
  }

  // Callback function to close the modal after saving
  const handleCloseModalAfterSave = () => {
    setOpen(false)
  }

  const role = useSelector((state: RootState) => state.auth.userRole)

  return (
    <React.Fragment>
      {/* Task Update Button */}
      {role !== 'teamMember' ? (
        <Tooltip title='Update' arrow>
          <IconButton
            id='task-update-button'
            color='primary'
            size='small'
            onClick={handleOpenModal}
            data-testid="task-update-button"
          >
            <EditRoundedIcon />
          </IconButton>
        </Tooltip>
      ) : (
        ''
      )}

      {/* Task Update Modal */}
      <Dialog open={open} fullWidth fullScreen={fullScreen}>
        <DialogTitle
          sx={{
            background: gradientColors.greenGradient,
            color: textColors.white,
            fontWeight: 'bold',
            fontSize: fontSizes.size20,
          }}
        >
          Update Task
        </DialogTitle>
        <IconButton
          onClick={handleCloseModal}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
            color: textColors.white,
          }}
          size='small'
          data-testid="task-update-close-button"
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <TaskForm
            taskDetails={taskDetails}
            onSave={handleCloseModalAfterSave}
            onChange={handleFormChanges}
          />
        </DialogContent>
      </Dialog>
      {ConfirmationAlert}
    </React.Fragment>
  )
}

export default TaskUpdateModal
